import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Checkbox.module.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** The visible text. Wrapped in the same <label> as the box, so clicking
   *  either one toggles — no htmlFor to keep in sync, and no way to ship a
   *  box whose label is not attached to it. */
  label: ReactNode;
  /** A quieter second line, for what turning this on will actually do. */
  hint?: ReactNode;
}

/**
 * A native checkbox with the system's own mark drawn over it.
 *
 * The input itself stays in the DOM and keeps every behaviour a checkbox has —
 * the space bar, the tab order, the form value, what a screen reader announces.
 * It is only made invisible; `appearance: none` on the real control is what
 * leaves a box that cannot be reached by keyboard the moment someone styles it
 * wrong.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, ...rest },
  ref,
) {
  return (
    <label className={cx(styles.row, className)}>
      <input ref={ref} type="checkbox" className={styles.input} {...rest} />
      <span className={styles.box} aria-hidden="true">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3.5 8.5l3 3 6-6" />
        </svg>
      </span>
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        {hint && <span className={styles.hint}>{hint}</span>}
      </span>
    </label>
  );
});
